import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Settings2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { StoreProfileDetail } from '../../types';
import { retailerStatusLabel, getRetailerStoreStatus } from '../../lib/retailerViewUtils';
import { photoObjectPosition } from '../../lib/photoFocalPoint';
import { useStorefrontImageSource } from '../../hooks/useStorefrontImageSource';
import { StorefrontPhotoUpload } from '../StorefrontPhotoUpload';

interface RetailerStoreHeaderProps {
  profile: StoreProfileDetail;
  storeIdParam: string;
  onManageTwin: () => void;
  canManageTwin?: boolean;
}

export function RetailerStoreHeader({
  profile,
  storeIdParam,
  onManageTwin,
  canManageTwin = true,
}: RetailerStoreHeaderProps) {
  const status = getRetailerStoreStatus(profile);
  const [heroLoadFailed, setHeroLoadFailed] = useState(false);
  const storefrontImage = useStorefrontImageSource(profile.heroImageUrl);
  const heroImageUrl = storefrontImage.resolvedUrl ?? undefined;
  const showHero = Boolean(heroImageUrl && !heroLoadFailed && !storefrontImage.hasError);

  useEffect(() => {
    setHeroLoadFailed(false);
  }, [heroImageUrl]);

  const identity = (
    <div className="min-w-0">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          className={showHero ? 'border-white/40 bg-black/25 text-white backdrop-blur-sm' : undefined}
        >
          {profile.retailer}
        </Badge>
        {profile.isMockData ? (
          <Badge className="border-violet-200 bg-violet-100 text-violet-800">Demo store</Badge>
        ) : null}
        {profile.storefrontPhotoVerificationStatus === 'to_be_confirmed' ? (
          <Badge className="border-amber-200 bg-amber-100 text-amber-900">
            Image to be confirmed
          </Badge>
        ) : null}
      </div>
      <h1 className={`truncate text-xl font-semibold ${showHero ? 'text-white drop-shadow-sm' : 'text-gray-900'}`}>
        {profile.storeName}
      </h1>
      <p className={`mt-0.5 flex items-center gap-1 text-sm ${showHero ? 'text-white/90' : 'text-gray-600'}`}>
        <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">{profile.fullAddress}</span>
      </p>
    </div>
  );

  return (
    <header className="overflow-hidden rounded-xl border bg-white">
      {showHero ? (
        <div className="relative h-32 sm:h-40">
          <img
            src={heroImageUrl}
            alt={`${profile.storeName} storefront`}
            className="h-full w-full object-cover"
            style={{ objectPosition: photoObjectPosition(profile.heroPhotoFocalPoint) }}
            onError={() => setHeroLoadFailed(true)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/5" />
          <div className="absolute inset-x-0 bottom-0 px-4 py-3 sm:px-5 sm:py-4">
            {identity}
          </div>
        </div>
      ) : (
        <div className="px-4 pt-3 sm:px-5">{identity}</div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5">
        <dl className="text-sm">
          <div className="flex items-center gap-2">
            <dt className="text-gray-500">Last verified</dt>
            <dd className="font-medium">{profile.lastSurveyDate ?? '—'}</dd>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <dt className="text-gray-500">Status</dt>
            <dd>
              <Badge
                variant="outline"
                className={
                  status === 'attention'
                    ? 'border-amber-300 bg-amber-50 text-amber-800'
                    : status === 'healthy'
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                      : 'border-gray-300 bg-gray-50 text-gray-700'
                }
              >
                {retailerStatusLabel(status)}
              </Badge>
            </dd>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <dt className="text-gray-500">Twin readiness</dt>
            <dd className="font-medium">{profile.twinReadiness.percent}%</dd>
          </div>
        </dl>

        <div className="flex flex-wrap items-center gap-2">
          {profile.storeId != null ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Storefront image</span>
              <StorefrontPhotoUpload
                storeId={profile.storeId}
                storeIdParam={storeIdParam}
                hasPhoto={profile.hasPhoto}
                imageUrl={profile.heroImageUrl}
                photoSource={profile.storefrontPhotoSource}
                verificationStatus={profile.storefrontPhotoVerificationStatus}
                sourcePageUrl={profile.storefrontPhotoSourcePageUrl}
                focalPoint={profile.heroPhotoFocalPoint}
                variant="menu"
                className="bg-slate-800 hover:bg-slate-700"
              />
            </div>
          ) : null}

          {canManageTwin && (
            <Button type="button" variant="outline" size="sm" onClick={onManageTwin} className="shrink-0">
              <Settings2 className="h-4 w-4 mr-1.5" aria-hidden />
              Manage Twin
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
